import { useMemo, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { retrieveContentExtensionsCount } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers';

type ContentBody = Parameters<RetrieveContentExtensionsCount>[0] | null | undefined;
type CustomPerfData =
	| { contentExtensionsCount?: ReturnType<RetrieveContentExtensionsCount> }
	| null
	| undefined;
type RetrieveContentExtensionsCount = typeof retrieveContentExtensionsCount;

export function useContentExtensionsCount(
	contentBody: ContentBody,
	customPerfData: CustomPerfData,
) {
	const skip = !customPerfData || fg('cc_complexit_fe_reduce_adf_parse');
	const contentExtensionsCount = useMemo(
		() => (contentBody && !skip ? retrieveContentExtensionsCount(contentBody) : {}),
		[contentBody, skip],
	);

	const memoRef = useRef({
		contentBody: undefined as typeof contentBody,
		contentExtensionsCount: undefined as undefined | typeof contentExtensionsCount,
	});

	if (customPerfData) {
		if (fg('cc_complexit_fe_reduce_adf_parse')) {
			if (
				memoRef.current.contentBody !== contentBody ||
				!('contentExtensionsCount' in customPerfData)
			) {
				// `retrieveContentExtensionsCount` parses the ADF string into JSON, but that's likely to be only one of multiple such parsings.
				// For example, the Atlaskit editor/renderer will very likely parse the ADF string into JSON as well.
				// For large documents (e.g. 16MB) such parsing can take noticeable time which is undesirable especially before rendering.
				// This allows an alternative to `retrieveContentExtensionsCount` to optionally perform the computation in a more performant way.
				// But if it doesn't, `retrieveContentExtensionsCount` will be the fallback.
				let propertyValue: undefined | typeof contentExtensionsCount;

				Object.defineProperty(customPerfData, 'contentExtensionsCount', {
					enumerable: true,
					get() {
						if (propertyValue) {
							return propertyValue;
						}

						memoRef.current.contentBody = contentBody;
						memoRef.current.contentExtensionsCount = contentBody
							? retrieveContentExtensionsCount(contentBody)
							: {};

						return memoRef.current.contentExtensionsCount;
					},
					set(value: typeof propertyValue) {
						propertyValue = value;
					},
				});
			}
		} else {
			customPerfData.contentExtensionsCount = contentExtensionsCount;
		}
	}
}
